//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import NavLogo from '../../icons/devops_loop_white_logo.png';
import loopGenieIcon from '../../icons/modern/loop_genie_icon.png';
import { navBarStyle } from '../../styles/navBarStyle';
import { useUserAuth } from '../../UserAuth';
import Switcher from '../switcher/switcher';
import withZoom from '../Zoom/withZoom';
import SearchBox from '../search/SearchBox';
import ProfileMenu from './ProfileMenu';
import SupportMenu from './SupportMenu';
import { useConfig } from '../../Config';
import { useLoopContext } from '../../LoopContext';
import AIModule from '../Loops/AI/AIModule';
import { useTheme } from '../../ThemeContext';
import { useAIModule } from '../../contexts/AIModuleContext';

function NavBar({
  userName,
  showInfoOptions = true,
  isZoomedIn,
  topOffset = 0,
}: {
  userName?: string;
  showInfoOptions?: boolean;
  isZoomedIn?: boolean;
  topOffset?: number;
}) {
  const { t } = useTranslation();
  const { hasBasicRole, isAuthenticating, isLoading, user, roles } =
    useUserAuth();
  const { services } = useConfig();
  const showSearch = services?.search?.show;
  const showAI = services?.analyticsai?.show;
  const { completedLoops } = useLoopContext();
  const { darkMode } = useTheme();
  const isAdmin = roles?.includes('ROLE_ADMIN') ?? false;
  const currentUsername = user?.profile?.preferred_username ?? '';
  const isUserInALoop = completedLoops.some((loop) =>
    loop.associatedUserIds?.includes(currentUsername),
  );
  const {
    isAIModuleVisible,
    setIsAIModuleVisible,
    chatHistories,
    setChatHistories,
  } = useAIModule();

  return (
    <nav
      style={{
        ...navBarStyle.navBar(isZoomedIn ?? false, darkMode),
        top: topOffset,
      }}
      data-testid="id-nav-bar"
      id="id-nav-bar"
      aria-label={t('nav-bar')}
    >
      {/* Left section - Logo and Brand */}
      <div
        className="flex items-center"
        id="id-navbar-left-section"
        data-testid="navbar-left-section"
      >
        <Link
          to="/"
          className="ease flex w-12 items-center justify-center pl-0.5 no-underline opacity-100 transition-opacity duration-300 hover:opacity-90"
        >
          <img
            src={NavLogo}
            alt={t('navHeading')}
            className="h-7 w-auto object-contain"
            id="nav-heading"
            data-testid="nav-heading"
            aria-label={t('navHeading')}
          />
        </Link>
        {/* Brand text */}
        <Link
          to="/"
          className="ease text-lg font-medium text-white no-underline opacity-100 transition-opacity duration-300 hover:opacity-90"
        >
          {t('devops-loop')}
        </Link>
      </div>

      {/* Right section - Utility items */}
      <div
        className="ml-auto flex items-center gap-1"
        id="id-right-logos"
        data-testid="right-logos"
      >
        {/* Search */}
        {showSearch && (
          <div
            className="mr-2.5"
            id="id-navbar-search-wrapper"
            data-testid="navbar-search-wrapper"
          >
            <SearchBox placeholder={t('searchbox-placeholder')} width="500px" />
          </div>
        )}

        {/* Loop Genie Button */}
        {showAI && (
          <button
            className="flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium text-white transition-opacity"
            style={{
              background: isUserInALoop
                ? 'linear-gradient(to right, #82A1EE, #784BF3)'
                : '#8b8b8b',
              opacity: isUserInALoop ? 1 : 0.6,
              cursor: isUserInALoop ? 'pointer' : 'not-allowed',
            }}
            id="loop-genie-button"
            data-testid="loop-genie-button"
            disabled={!isUserInALoop}
            onClick={() => {
              if (!isUserInALoop) {
                return;
              }
              if (isAIModuleVisible) {
                setIsAIModuleVisible(false);
                setTimeout(() => setIsAIModuleVisible(true), 0);
              } else {
                setIsAIModuleVisible(true);
              }
            }}
            title={
              isUserInALoop
                ? t('loop-genie')
                : t(
                    isAdmin
                      ? 'create-loop-to-use-genie'
                      : 'create-loop-to-use-genie-member',
                  )
            }
          >
            <img
              src={loopGenieIcon}
              alt="Loop Genie"
              className="size-5 object-contain"
            />
            {t('loop-genie')}
          </button>
        )}

        {/* Support Menu */}
        {hasBasicRole && showInfoOptions && (
          <div
            className="relative"
            id="id-navbar-support-menu-container"
            data-testid="navbar-support-menu-container"
          >
            <SupportMenu userName={userName} />
          </div>
        )}

        {/* Profile Menu */}
        <div
          className="relative"
          id="id-navbar-profile-menu-container"
          data-testid="navbar-profile-menu-container"
        >
          <ProfileMenu
            userName={userName}
            isAuthenticating={isAuthenticating}
            isLoading={isLoading}
          />
        </div>

        {/* Switcher */}
        <div
          className="relative"
          id="id-navbar-switcher-container"
          data-testid="navbar-switcher-container"
        >
          <Switcher hasNexusRole={hasBasicRole} t={t} />
        </div>
      </div>

      {showAI && (
        <AIModule
          isVisible={isAIModuleVisible}
          onClose={() => setIsAIModuleVisible(false)}
          availableLoops={completedLoops.map((loop) => ({
            id: loop.id,
            name: loop.displayName,
          }))}
          initialChatHistories={chatHistories}
          setChatHistories={setChatHistories}
        />
      )}
    </nav>
  );
}

export default withZoom(NavBar);
